// Auto-discovery des modules — isolé dans son propre module pour éviter les cycles
// avec App.tsx et home.tsx (qui lisent tous deux discoveredModules).
//
// Vite remplace import.meta.glob au build par un map statique de tous les fichiers matchant.

import type { ReactNode } from 'react';

export interface ModuleChildRoute {
  readonly path: string;
  readonly label: string;
  readonly icon?: string;
  readonly element: ReactNode;
}

export interface ModuleRoute {
  /** Chemin relatif sans slash de tête, ex: 'commandes' ou 'kpi/entrepot' */
  readonly path: string;
  /** Libellé affiché dans la TopBar et fil d'ariane */
  readonly label: string;
  /** Nom d'icône lucide-react (ex: 'shopping-cart') */
  readonly icon: string;
  /** Élément React à rendre */
  readonly element: ReactNode;
  /** Sous-routes optionnelles affichées dans la sidebar */
  readonly children?: ReadonlyArray<ModuleChildRoute>;
  /**
   * Masque l'onglet de la TopBar tout en gardant la route active (Ben 22/07,
   * `herbo-lot-detail`) — utile quand un module expose 2 routes top-level
   * (ex. liste + fiche détail paramétrée) mais qu'un seul onglet nav a du
   * sens (une fiche `:numLot` sans numéro connu n'est pas un point
   * d'atterrissage valide). Par défaut `false` — comportement inchangé pour
   * tous les autres modules.
   */
  readonly hidden?: boolean;
  /**
   * Compteur optionnel sur l'onglet (ajout 23/09, parcours factures) : le
   * shell lit `url` (GET JSON, même session) et affiche `reponse[champ]` s'il
   * vaut plus de 0 — `ton` = `danger` (rouge, ex. « problème en cours ») ou
   * `warn` (ambre, ex. factures à arbitrer). Absent = aucun compteur.
   */
  readonly badge?: { readonly url: string; readonly champ: string; readonly ton: 'danger' | 'warn' };
  /**
   * Place l'onglet juste après celui dont le `path` est donné (ajout 28/10,
   * « Fiches neuves » à côté de « Changements de prix ») ; cible inconnue =
   * ordre de découverte inchangé.
   */
  readonly apres?: string;
}

interface ModuleManifest {
  readonly default: ModuleRoute | ReadonlyArray<ModuleRoute>;
}

function loadDiscoveredModules(): ModuleRoute[] {
  // Chemin depuis apps/web/src/routes/ jusqu'à la racine du monorepo (DB-Herbo/) :
  // ../ = src, ../../ = apps/web, ../../../ = apps, ../../../../ = racine → modules/
  const found = import.meta.glob<ModuleManifest>('../../../../modules/*/web/route.tsx', {
    eager: true,
  });
  const out: ModuleRoute[] = [];
  for (const mod of Object.values(found)) {
    const def = mod.default;
    if (!def) continue;
    // `Array.isArray` ne sait pas rétrécir un `ReadonlyArray` (il ne reconnaît
    // que `any[]`), d'où TS2345 sur la branche `else`. On teste donc la forme
    // explicitement : un module expose soit une route, soit un tableau de routes.
    if (isTableauDeRoutes(def)) out.push(...def);
    else out.push(def);
  }
  return placerApres(out);
}

/** Déplace chaque route portant `apres` juste derrière sa cible (si elle existe). */
export function placerApres(routes: ReadonlyArray<ModuleRoute>): ModuleRoute[] {
  const out = routes.filter((r) => !r.apres || !routes.some((c) => c.path === r.apres));
  for (const r of routes) {
    if (!r.apres || out.includes(r)) continue;
    const i = out.findIndex((c) => c.path === r.apres);
    out.splice(i + 1, 0, r);
  }
  return out;
}

function isTableauDeRoutes(
  def: ModuleRoute | ReadonlyArray<ModuleRoute>,
): def is ReadonlyArray<ModuleRoute> {
  return Array.isArray(def);
}

export const discoveredModules: ReadonlyArray<ModuleRoute> = loadDiscoveredModules();
